import {
  commonerStatblock,
  MAP_TYPE_KEY,
  createResolver,
  naturalCompare,
  normalizeSheet,
  sameJson,
  sheetOf,
  statblockOf,
  typeHasStatblock,
  type NoteDetail,
  type CharacterSheet,
  type Statblock,
} from '@kartothek/shared';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { api, ApiError } from '../api';
import { Backlinks } from '../components/Backlinks';
import { CharacterSheetView, Pips, SheetEditor } from '../components/CharacterSheet';
import { Editor } from '../components/Editor';
import { MapView } from '../components/MapView';
import { TokenImageControl } from '../components/TokenImage';
import { MarkdownView } from '../components/MarkdownView';
import { useNewNote } from '../components/NewNoteDialog';
import { NoteLink } from '../components/NoteLink';
import { Properties } from '../components/Properties';
import { RevealControl } from '../components/RevealControl';
import { StatblockEditor, StatblockView } from '../components/Statblock';
import { useCampaign, useInvalidateCampaign, useNote, useNoteIndex, useTypes } from '../hooks';
import { useOfflineState } from '../offline';
import { t, tDyn } from '../i18n';
import { classNames, splitTarget } from '../util';

type Draft = Pick<NoteDetail, 'title' | 'folder' | 'typeKey' | 'visibility' | 'frontmatter' | 'body'>;

const AUTOSAVE_MS = 1500;

export function NotePage() {
  const { cid = '', nid = '' } = useParams();
  const note = useNote(cid, nid);
  const campaign = useCampaign(cid);
  const [params, setParams] = useSearchParams();
  const editing = params.get('edit') === '1';
  const gm = !!campaign.data && campaign.data.role !== 'player';
  // Spieler bearbeiten eigene Notizen und ihren zugewiesenen Charakter
  const canEdit = gm || !!note.data?.canEdit;
  const location = useLocation();
  useMarkSeen(cid, nid, !!campaign.data && !gm);

  const setEditing = useCallback(
    (on: boolean) => {
      const next = new URLSearchParams(params);
      if (on) next.set('edit', '1');
      else next.delete('edit');
      setParams(next, { replace: true });
    },
    [params, setParams],
  );

  // Strg+E schaltet zwischen Lesen und Bearbeiten
  useEffect(() => {
    if (!canEdit) return;
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'e') {
        e.preventDefault();
        setEditing(!editing);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [editing, canEdit, setEditing]);

  // Zu `#überschrift` scrollen
  useEffect(() => {
    if (!note.data || !location.hash) return;
    const el = document.getElementById(decodeURIComponent(location.hash.slice(1)));
    el?.scrollIntoView({ block: 'start' });
  }, [note.data, location.hash]);

  useEffect(() => {
    if (note.data) document.title = `${note.data.title} · Kartothek`;
  }, [note.data]);

  if (note.isLoading) return <div className="muted pad">{t('common.loading')}</div>;
  if (note.isError || !note.data) return <div className="pad error">{t('note.notFound')}</div>;

  return editing && canEdit ? (
    <NoteEditor key={nid} note={note.data} gm={gm} onDone={() => setEditing(false)} />
  ) : (
    <NoteReader note={note.data} gm={gm} onEdit={() => setEditing(true)} />
  );
}

/** Spieler öffnet eine freigegebene, noch ungesehene Notiz → nicht mehr „neu“. */
function useMarkSeen(cid: string, nid: string, player: boolean) {
  const index = useNoteIndex(cid);
  const qc = useQueryClient();
  const unseen = player && !!index.data?.find((n) => n.id === nid)?.unseen;
  useEffect(() => {
    if (!unseen) return;
    void api
      .markSeen(cid, nid)
      .then(() => {
        void qc.invalidateQueries({ queryKey: ['notes', cid] });
        void qc.invalidateQueries({ queryKey: ['discoveries', cid] });
      })
      .catch(() => {
        /* offline: beim nächsten Öffnen erneut */
      });
  }, [cid, nid, unseen, qc]);
}

function NoteHeader({ note }: { note: NoteDetail }) {
  const types = useTypes(note.campaignId);
  const type = types.byKey.get(note.typeKey);
  return (
    <div className="note-meta">
      <span className="type-badge" style={{ ['--chip' as string]: type?.color }}>
        {type?.label ?? note.typeKey}
      </span>
      {note.folder && <span className="muted small">{note.folder}</span>}
      <span className={`vis vis-${note.visibility}`}>{tDyn(`visibility.${note.visibility}`)}</span>
      {sheetOf(note.frontmatter).dead && <span className="dead-badge">✝ {t('combat.dead')}</span>}
    </div>
  );
}

/** Pfad über das Feld „Liegt in“: Schwertküste › Niewinter › Taverne. */
function Breadcrumbs({ note }: { note: NoteDetail }) {
  const parents = note.hierarchy?.parents ?? [];
  if (parents.length === 0) return null;
  return (
    <nav className="breadcrumbs" aria-label="Pfad">
      {parents.map((p) => (
        <span key={p.id}>
          <NoteLink cid={note.campaignId} noteId={p.id}>
            {p.title}
          </NoteLink>
          <span className="sep">›</span>
        </span>
      ))}
      <span className="current">{note.title}</span>
    </nav>
  );
}

function Children({ note }: { note: NoteDetail }) {
  const types = useTypes(note.campaignId);
  const children = [...(note.hierarchy?.children ?? [])].sort((a, b) => naturalCompare(a.title, b.title));
  if (children.length === 0) return null;
  return (
    <section className="children">
      <h3>
        {t('hierarchy.children')} <span className="count">{children.length}</span>
      </h3>
      <ul>
        {children.map((c) => (
          <li key={c.id}>
            <span className="type-dot" style={{ background: types.byKey.get(c.typeKey)?.color }} />
            <NoteLink cid={note.campaignId} noteId={c.id}>
              {c.title}
            </NoteLink>
          </li>
        ))}
      </ul>
    </section>
  );
}

/** Bogendaten (`sheet` im Frontmatter) speichern: sofort anzeigen, dann ohne Konflikt mit dem Text patchen. */
function useSaveSheet(note: NoteDetail) {
  const qc = useQueryClient();
  return (sheet: CharacterSheet) => {
    qc.setQueryData<NoteDetail>(['note', note.campaignId, note.id], (old) => (old ? { ...old, frontmatter: { ...old.frontmatter, sheet } } : old));
    const empty = Object.keys(sheet).length === 0;
    void api
      .patchFrontmatter(note.campaignId, note.id, empty ? {} : { sheet }, empty ? ['sheet'] : [])
      .then((res) => {
        qc.setQueryData(['note', note.campaignId, note.id], res);
        void qc.invalidateQueries({ queryKey: ['items', note.campaignId] });
      })
      .catch((e: unknown) => {
        // Abgelehnt (z. B. verstorben): Anzeige auf den Serverstand zurücksetzen
        void qc.invalidateQueries({ queryKey: ['note', note.campaignId, note.id] });
        alert(e instanceof ApiError ? e.message : String(e));
      });
  };
}

/** NSC: Todesrettungswürfe und „Verstorben“ (Charaktere haben beides im Bogen). */
function NpcLifeStatus({ note }: { note: NoteDetail }) {
  const sheet = sheetOf(note.frontmatter);
  const save = useSaveSheet(note);
  const set = (patch: Partial<CharacterSheet>) => save(normalizeSheet({ ...sheet, ...patch }));
  const ds = sheet.deathSaves ?? { success: 0, failure: 0 };
  return (
    <div className="life-status">
      <span className="small muted">{t('sheet.deathSaves')}</span>
      <span className="cb-death">
        ✓
        <Pips total={3} filled={ds.success} tone="ok" label={t('sheet.deathSuccess')} onChange={sheet.dead ? undefined : (n) => set({ deathSaves: { ...ds, success: n } })} />
        ✗
        <Pips total={3} filled={ds.failure} tone="bad" label={t('sheet.deathFailure')} onChange={sheet.dead ? undefined : (n) => set({ deathSaves: { ...ds, failure: n } })} />
      </span>
      <button
        className={classNames('small', sheet.dead && 'on')}
        onClick={() => {
          if (!sheet.dead && !confirm(t('combat.deadConfirm', { name: note.title }))) return;
          set(sheet.dead ? { dead: false, deathSaves: { success: 0, failure: 0 } } : { dead: true });
        }}
      >
        ✝ {sheet.dead ? t('combat.undoDead') : t('combat.dead')}
      </button>
    </div>
  );
}

/** Statblock in der Leseansicht, bzw. Knopf zum Anlegen bei Kreaturen ohne Statblock. */
function ReaderStatblock({ note, gm }: { note: NoteDetail; gm: boolean }) {
  const types = useTypes(note.campaignId);
  const qc = useQueryClient();
  const campaign = useCampaign(note.campaignId);
  const saveSheet = useSaveSheet(note);
  const sb = statblockOf(note.frontmatter);
  if (sb && note.typeKey === 'charakter') {
    return (
      <CharacterSheetView
        note={note}
        statblock={sb}
        sheet={sheetOf(note.frontmatter)}
        defaultFormat={campaign.data?.ruleset === '5e-2024' ? '2024' : '2014'}
        editable={gm || note.canEdit}
        onSheet={saveSheet}
      />
    );
  }
  if (sb) {
    return <StatblockView statblock={sb} fallbackName={note.title} cid={note.campaignId} links={note.links} folder={note.folder} isGameMaster={gm} />;
  }
  if (!gm || !typeHasStatblock(types.byKey.get(note.typeKey)?.fields)) return null;
  const create = async () => {
    const res = await api.updateNote(note.campaignId, note.id, {
      frontmatter: { ...note.frontmatter, statblock: commonerStatblock(note.title) },
      baseUpdatedAt: note.updatedAt,
    });
    qc.setQueryData(['note', note.campaignId, note.id], res);
  };
  return (
    <div className="statblock-missing">
      <span className="muted">{t('statblock.none')}</span> <button onClick={() => void create()}>+ {t('statblock.create')}</button>
    </div>
  );
}

function NoteReader({ note, gm, onEdit }: { note: NoteDetail; gm: boolean; onEdit: () => void }) {
  const types = useTypes(note.campaignId);
  return (
    <article className="note parchment">
      <header className="note-head">
        <div>
          <NoteHeader note={note} />
          <Breadcrumbs note={note} />
          <h1 className="note-title">{note.title}</h1>
        </div>
        {(gm || note.canEdit) && (
          <div className="row note-actions">
            {gm && <RevealControl note={note} />}
            <button onClick={onEdit} title="Strg+E">
              ✎ {t('common.edit')}
            </button>
          </div>
        )}
      </header>
      <Properties
        cid={note.campaignId}
        frontmatter={note.frontmatter}
        type={types.byKey.get(note.typeKey)}
        editing={false}
        links={note.links}
        folder={note.folder}
        isGameMaster={gm}
        hideKeys={
          note.typeKey === 'charakter' && note.frontmatter.statblock
            ? ['player', 'class', 'level', 'species', 'background']
            : note.typeKey === MAP_TYPE_KEY
              ? ['image']
              : undefined
        }
      />
      {note.typeKey === MAP_TYPE_KEY && <MapView note={note} gm={gm} />}
      {gm && ['monster', 'nsc', 'charakter'].includes(note.typeKey) && <TokenImageControl note={note} />}
      {gm && note.typeKey === 'nsc' && <NpcLifeStatus note={note} />}
      <ReaderStatblock note={note} gm={gm} />
      <MarkdownView note={note} isGameMaster={gm} />
      <Children note={note} />
      <Backlinks note={note} />
    </article>
  );
}

function NoteEditor({ note, gm, onDone }: { note: NoteDetail; gm: boolean; onDone: () => void }) {
  const cid = note.campaignId;
  const qc = useQueryClient();
  const navigate = useNavigate();
  const invalidate = useInvalidateCampaign(cid);
  const index = useNoteIndex(cid);
  const types = useTypes(cid);
  const newNote = useNewNote();
  const offline = useOfflineState();

  const [draft, setDraft] = useState<Draft>(() => ({
    title: note.title,
    folder: note.folder,
    typeKey: note.typeKey,
    visibility: note.visibility,
    frontmatter: note.frontmatter,
    body: note.body,
  }));
  const [status, setStatus] = useState<'idle' | 'dirty' | 'saving' | 'saved' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);
  const saved = useRef<NoteDetail>(note);
  const draftRef = useRef(draft);
  draftRef.current = draft;
  const timer = useRef<number | undefined>(undefined);
  const queue = useRef<Promise<void>>(Promise.resolve());

  const folders = useMemo(
    () => [...new Set((index.data ?? []).map((n) => n.folder).filter(Boolean))].sort(naturalCompare),
    [index.data],
  );

  // Speichervorgänge laufen strikt nacheinander (sonst kollidiert der zweite mit dem ersten → „inzwischen geändert“)
  const save = useCallback((): Promise<void> => {
    window.clearTimeout(timer.current);
    const run = async () => {
      const d = draftRef.current;
      const s = saved.current;
      const changed =
        d.title !== s.title ||
        d.folder !== s.folder ||
        d.typeKey !== s.typeKey ||
        d.visibility !== s.visibility ||
        d.body !== s.body ||
        // Reihenfolge-unabhängig: der Server (JSONB) sortiert Schlüssel um
        !sameJson(d.frontmatter, s.frontmatter);
      if (!changed) {
        setStatus((st) => (st === 'error' ? st : 'saved'));
        return;
      }
      const structural = d.title !== s.title || d.folder !== s.folder || d.typeKey !== s.typeKey || d.visibility !== s.visibility;
      setStatus('saving');
      try {
        const res = await api.updateNote(cid, note.id, { ...d, baseUpdatedAt: s.updatedAt });
        saved.current = res;
        qc.setQueryData(['note', cid, note.id], res);
        // Server kann Selbstverweise beim Umbenennen umgeschrieben haben
        if (draftRef.current === d && (res.body !== d.body || res.title !== d.title)) {
          setDraft((x) => ({ ...x, title: res.title, folder: res.folder, body: res.body, frontmatter: res.frontmatter }));
        }
        if (structural) invalidate();
        else void qc.invalidateQueries({ queryKey: ['notes', cid] });
        setError(null);
        setStatus(draftRef.current === d ? 'saved' : 'dirty');
      } catch (e) {
        setStatus('error');
        setError(e instanceof ApiError ? (e.code === 'stale' ? t('note.stale') : e.message) : String(e));
      }
    };
    const p = queue.current.then(run, run);
    queue.current = p;
    return p;
  }, [cid, note.id, qc, invalidate]);
  const saveRef = useRef(save);
  saveRef.current = save;

  const update = (patch: Partial<Draft>, immediate = false) => {
    setDraft((d) => ({ ...d, ...patch }));
    setStatus('dirty');
    window.clearTimeout(timer.current);
    if (immediate) queueMicrotask(() => void save());
    else timer.current = window.setTimeout(() => void save(), AUTOSAVE_MS);
  };

  // Beim Verlassen ungespeicherte Änderungen sichern
  useEffect(() => {
    const beforeUnload = (e: BeforeUnloadEvent) => {
      if (draftRef.current.body !== saved.current.body) e.preventDefault();
    };
    window.addEventListener('beforeunload', beforeUnload);
    // Nur beim Verlassen des Editors speichern – nicht bei jedem Neuzeichnen
    return () => {
      window.removeEventListener('beforeunload', beforeUnload);
      void saveRef.current();
    };
  }, []);

  const resolver = useMemo(
    () => createResolver((index.data ?? []).map((n) => ({ id: n.id, folder: n.folder, title: n.title }))),
    [index.data],
  );

  const openLink = (target: string) => {
    const r = resolver.resolve(target, draft.folder);
    if (r) {
      void save().then(() => navigate(`/c/${cid}/n/${r.id}`));
      return;
    }
    const { folder, title } = splitTarget(target);
    newNote.open({ title, folder, onCreated: (id) => void save().then(() => navigate(`/c/${cid}/n/${id}?edit=1`)) });
  };

  const remove = async () => {
    if (!confirm(t('note.deleteConfirm', { title: note.title }))) return;
    window.clearTimeout(timer.current);
    await api.deleteNote(cid, note.id);
    qc.removeQueries({ queryKey: ['note', cid, note.id] });
    invalidate();
    navigate(`/c/${cid}`);
  };

  const done = async () => {
    await save();
    if (draftRef.current.body === saved.current.body) onDone();
  };

  return (
    <article className="note editing">
      <header className="note-head">
        <input
          className="title-input"
          value={draft.title}
          onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))}
          onBlur={() => draft.title.trim() && draft.title !== saved.current.title && update({ title: draft.title.trim() }, true)}
          onKeyDown={(e) => e.key === 'Enter' && (e.currentTarget as HTMLInputElement).blur()}
          aria-label={t('note.title')}
          title={t('note.renameHint')}
        />
        <div className="note-actions">
          <span className={`save-status ${status}`}>
            {status === 'saving'
              ? t('common.saving')
              : status === 'dirty'
                ? t('note.unsaved')
                : status === 'saved'
                  ? offline.pending > 0
                    ? t('offline.savedLocal')
                    : t('common.saved')
                  : status === 'error'
                    ? t('common.error')
                    : ''}
          </span>
          {note.canDelete && (
            <button className="danger" onClick={remove}>
              {t('common.delete')}
            </button>
          )}
          <button className="primary" onClick={done} title="Strg+E">
            ✓ {t('common.read')}
          </button>
        </div>
      </header>
      {error && (
        <div className="error banner">
          {error}{' '}
          <button
            onClick={() => {
              void qc.invalidateQueries({ queryKey: ['note', cid, note.id] });
              onDone();
            }}
          >
            ↻
          </button>
        </div>
      )}
      <div className="meta-form">
        {gm && (
          <label>
            {t('note.folder')}
            <input
              list="editor-folders"
              value={draft.folder}
              placeholder={t('nav.root')}
              onChange={(e) => setDraft((d) => ({ ...d, folder: e.target.value }))}
              onBlur={() => draft.folder !== saved.current.folder && update({ folder: draft.folder.trim() }, true)}
            />
            <datalist id="editor-folders">
              {folders.map((f) => (
                <option key={f} value={f} />
              ))}
            </datalist>
          </label>
        )}
        {gm && (
          <label>
            {t('note.type')}
            <select value={draft.typeKey} onChange={(e) => update({ typeKey: e.target.value }, true)}>
              {(types.data ?? []).map((ty) => (
                <option key={ty.key} value={ty.key}>
                  {ty.label}
                </option>
              ))}
            </select>
          </label>
        )}
        <label>
          {t('note.visibility')}
          {gm ? (
            <select value={draft.visibility} onChange={(e) => update({ visibility: e.target.value as Draft['visibility'] }, true)}>
              <option value="dm">{t('visibility.dm')}</option>
              <option value="all">{t('visibility.all')}</option>
              {draft.visibility === 'players' && <option value="players">{t('visibility.players')}</option>}
            </select>
          ) : (
            <select value={draft.visibility === 'all' ? 'all' : 'players'} onChange={(e) => update({ visibility: e.target.value as Draft['visibility'] }, true)}>
              <option value="players">{t('visibility.private')}</option>
              <option value="all">{t('visibility.all')}</option>
            </select>
          )}
        </label>
      </div>
      <Properties
        cid={cid}
        frontmatter={draft.frontmatter}
        type={types.byKey.get(draft.typeKey)}
        editing
        onChange={(fm) => update({ frontmatter: fm })}
        isGameMaster={gm}
      />
      <EditorStatblock
        note={note}
        title={draft.title}
        frontmatter={draft.frontmatter}
        creature={typeHasStatblock(types.byKey.get(draft.typeKey)?.fields)}
        onChange={(sb) => {
          const fm = { ...draft.frontmatter };
          if (sb) fm.statblock = sb;
          else delete fm.statblock;
          update({ frontmatter: fm });
        }}
        onSheet={(sheet) => update({ frontmatter: { ...draft.frontmatter, sheet } })}
      />
      <Editor
        value={draft.body}
        onChange={(body) => update({ body })}
        onSave={() => void save()}
        onOpenLink={openLink}
        getIndex={() => index.data ?? []}
        onUploadFile={
          gm
            ? async (file) => {
                const res = await api.uploadFile(cid, file);
                return res.name;
              }
            : undefined
        }
      />
    </article>
  );
}

function EditorStatblock(props: {
  note: NoteDetail;
  title: string;
  frontmatter: Record<string, unknown>;
  creature: boolean;
  onChange: (sb: Statblock | undefined) => void;
  onSheet: (sheet: CharacterSheet) => void;
}) {
  const sb = statblockOf(props.frontmatter);
  const campaign = useCampaign(props.note.campaignId);
  if (!sb && !props.creature) return null;
  const isCharacter = props.note.typeKey === 'charakter';
  // Verstorbene: Spielwerte gesperrt (Inventar in der Leseansicht)
  if (sheetOf(props.frontmatter).dead) return <div className="cs-dead-hint small">✝ {t('sheet.deadLocked')}</div>;
  return (
    <>
    {isCharacter && sb && (
      <details className="sb-details">
        <summary>{t('sheet.title')}</summary>
        <SheetEditor
          sheet={sheetOf(props.frontmatter)}
          defaultFormat={campaign.data?.ruleset === '5e-2024' ? '2024' : '2014'}
          onChange={(sheet) => props.onSheet(sheet)}
        />
      </details>
    )}
    <details className="sb-details" open={!!sb && !isCharacter}>
      <summary>{t('statblock.title')}</summary>
      {sb ? (
        <StatblockEditor
          statblock={sb}
          noteTitle={props.title}
          onChange={props.onChange}
          cid={props.note.campaignId}
          links={props.note.links}
          folder={props.note.folder}
          isGameMaster
        />
      ) : (
        <button type="button" onClick={() => props.onChange(commonerStatblock(props.title))}>
          + {t('statblock.create')}
        </button>
      )}
    </details>
    </>
  );
}
